/**
 * Detection / Rules - endpoint downloads and removable media (migration 0197).
 *
 * Live data sources:
 *   GET /v1/edge/fleet-policy/media          the tenant's and the units' settings,
 *                                            and the statement of what is
 *                                            enforced and what is detected
 *   PUT /v1/edge/fleet-policy/media          set one level (audited)
 *   GET /v1/detection/endpoint-media         findings raised, coverage, the
 *                                            sweep's place, delivery backlog
 *   GET /v1/edge/fleet-policy/media/mdm-export  laptops holding restricted
 *                                            data and the MDM setting
 *
 * Truthful scope: FOCTTA blocks nothing on the endpoint. A file holding a
 * restricted class found by the laptop agent's scan on removable media or in
 * a Downloads folder raises a finding. Blocking writes to removable disks is
 * the customer's MDM, with the setting and the device list this panel gives.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, Usb } from 'lucide-react';

import { GlassCard, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/access';
import {
  getEndpointMediaMdmExport,
  getEndpointMediaPolicy,
  getEndpointMediaSummary,
  listBusinessUnits,
  setEndpointMediaPolicy,
  type EndpointMediaMdmExport,
  type EndpointMediaPolicyRow,
  type EndpointMediaStatement,
  type EndpointMediaSummary,
} from '@/lib/api';

/** The fleet policy's write permission (PUT /v1/edge/fleet-policy/media). */
export const MEDIA_POLICY_WRITE_PERMISSION = 'foundational:connector:write';

const INPUT_CLASS = 'glass-lite rounded-md px-2 py-1 bg-transparent border border-border/40 text-xs';
const CLASS_RE = /^[a-z0-9_]{1,64}$/;
export const MAX_CLASSES = 50;

const KIND_LABEL = { removable_media: 'Removable media', download: 'Downloads folder' } as const;

function when(iso: string | null): string {
  return iso ? `${new Date(iso).toLocaleString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })} IST` : 'never';
}

/** "pan, Aadhaar ,pan" -> ['aadhaar', 'pan'], or the reason the controller would refuse it. */
export function parseClasses(text: string): { ok: true; value: string[] } | { ok: false; error: string } {
  const parts = text
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p.length > 0);
  const bad = parts.find((p) => !CLASS_RE.test(p));
  if (bad !== undefined) return { ok: false, error: `"${bad.slice(0, 70)}" is not a classifier label (a to z, 0 to 9 and _, at most 64 characters)` };
  const value = [...new Set(parts)].sort();
  if (value.length > MAX_CLASSES) return { ok: false, error: `at most ${MAX_CLASSES} classes` };
  return { ok: true, value };
}

export type TriState = 'inherit' | 'on' | 'off';
const triOf = (v: boolean | null | undefined): TriState => (v === true ? 'on' : v === false ? 'off' : 'inherit');
const boolOfTri = (t: TriState): boolean | null => (t === 'inherit' ? null : t === 'on');

/** What the editor sends for one level: every key, null where it inherits. */
export function editorBody(input: {
  scope: string;
  classesInherit: boolean;
  classes: string[];
  scan: TriState;
  watch: TriState;
  reason: string;
}): {
  scopeType: 'tenant' | 'business_unit';
  businessUnitId?: string;
  restrictedClasses: string[] | null;
  scanRemovable: boolean | null;
  watchDownloads: boolean | null;
  reason: string;
} {
  const tenant = input.scope === 'tenant';
  return {
    scopeType: tenant ? 'tenant' : 'business_unit',
    ...(tenant ? {} : { businessUnitId: input.scope }),
    restrictedClasses: input.classesInherit ? null : input.classes,
    scanRemovable: boolOfTri(input.scan),
    watchDownloads: boolOfTri(input.watch),
    reason: input.reason.trim(),
  };
}

/** A level's value in words: null inherits. */
export function levelText(v: boolean | string[] | null): string {
  if (v === null) return 'inherits';
  if (Array.isArray(v)) return v.length === 0 ? 'none' : v.join(', ');
  return v ? 'on' : 'off';
}

/** A CSV cell a spreadsheet will not run as a formula. */
function csvCell(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** The device list as CSV, for an MDM group import or by hand. */
export function mdmCsv(e: Pick<EndpointMediaMdmExport, 'devices'>): string {
  const lines = ['device,restricted_files,classes'];
  for (const d of e.devices) lines.push([csvCell(d.deviceKey), String(d.restrictedFiles), csvCell(d.classes.join(' '))].join(','));
  return `${lines.join('\r\n')}\r\n`;
}

export function MediaScopeNote({ statement }: { statement: EndpointMediaStatement | null }) {
  if (!statement) return null;
  return (
    <div className="space-y-1 text-xs" data-testid="endpoint-media-scope">
      <p className="font-medium">Enforced: {statement.enforced}</p>
      <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
        {statement.detected.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>
      <p className="text-muted-foreground">Downloads: {statement.downloads}</p>
    </div>
  );
}

export function MediaPolicyTable({ tenant, units }: { tenant: EndpointMediaPolicyRow | null; units: EndpointMediaPolicyRow[] }) {
  const rows = [tenant ?? null, ...units].filter((r): r is EndpointMediaPolicyRow => r !== null);
  if (rows.length === 0) {
    return (
      <CardDescription className="p-4 text-sm" data-testid="endpoint-media-no-policy">
        Nothing is set: no class is restricted, removable media is not scanned and Downloads folders are not watched.
      </CardDescription>
    );
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
          <th className="text-left px-4 py-2 font-medium">Level</th>
          <th className="text-left px-2 py-2 font-medium">Restricted classes</th>
          <th className="text-left px-2 py-2 font-medium">Scan removable media</th>
          <th className="text-left px-2 py-2 font-medium">Watch Downloads</th>
          <th className="text-left px-4 py-2 font-medium">Changed</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.businessUnitId ?? 'tenant'} className="border-b border-border/30" data-testid="endpoint-media-policy-row">
            <td className="px-4 py-2 font-medium">{r.scopeType === 'tenant' ? 'Whole tenant' : (r.businessUnitName ?? r.businessUnitId)}</td>
            <td className="px-2 py-2 font-mono text-[11px]">{levelText(r.restrictedClasses)}</td>
            <td className="px-2 py-2">{levelText(r.scanRemovable)}</td>
            <td className="px-2 py-2">{levelText(r.watchDownloads)}</td>
            <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{when(r.updatedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function MediaSummaryView({ s }: { s: EndpointMediaSummary }) {
  const count = (k: 'removable_media' | 'download') => s.counts.find((c) => c.kind === k);
  return (
    <div className="space-y-3" data-testid="endpoint-media-summary">
      <div className="grid grid-cols-2 gap-3 text-xs">
        {(['removable_media', 'download'] as const).map((k) => (
          <div key={k} className="glass-lite rounded-md p-2">
            <div className="font-medium">{KIND_LABEL[k]}</div>
            <div className="tabular-nums text-muted-foreground">
              {count(k)?.last24h ?? 0} in 24 h, {count(k)?.last7d ?? 0} in 7 days, {count(k)?.total ?? 0} in all
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="endpoint-media-coverage">
        {s.coverage.scanningRemovable} of {s.coverage.laptops} laptop{s.coverage.laptops === 1 ? '' : 's'} report scanning removable media;{' '}
        {s.coverage.laptops - s.coverage.reporting} have not reported the setting (an older agent, or no check-in since) and do not scan removable media. Last
        sweep {when(s.sweep.lastPassAt)}.
      </p>
      {s.deliveryBacklog.pending > 0 && (
        <div className="glass-lite rounded-md p-2 text-xs text-sev-high" role="alert" data-testid="endpoint-media-backlog">
          Not delivered yet: {s.deliveryBacklog.pending} audit event or notification{s.deliveryBacklog.pending === 1 ? '' : 's'} (oldest{' '}
          {when(s.deliveryBacklog.oldestAt)}). They are retried every minute; if this number does not go down, contact support@foctta.com.
        </div>
      )}
      {s.recent.length === 0 ? (
        <CardDescription className="text-sm" data-testid="endpoint-media-empty">
          No finding yet. This does not show that no restricted data is on removable media: only removable media
          connected to a laptop that scans it, at scan time, is checked (see the line above).
        </CardDescription>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
              <th className="text-left px-2 py-2 font-medium">Found</th>
              <th className="text-left px-2 py-2 font-medium">Where</th>
              <th className="text-left px-2 py-2 font-medium">Classes</th>
              <th className="text-left px-2 py-2 font-medium">Severity</th>
              <th className="text-left px-2 py-2 font-medium">Business unit</th>
              <th className="text-left px-2 py-2 font-medium">Decision</th>
            </tr>
          </thead>
          <tbody>
            {s.recent.map((r) => (
              <tr key={`${r.assetId}-${r.kind}`} className="border-b border-border/30" data-testid="endpoint-media-recent-row">
                <td className="px-2 py-2 whitespace-nowrap">{when(r.detectedAt)}</td>
                <td className="px-2 py-2">{KIND_LABEL[r.kind]}</td>
                <td className="px-2 py-2 font-mono text-[11px]">{r.dataClasses.join(', ')}</td>
                <td className="px-2 py-2">
                  <Badge variant="outline" className="text-[9px]">
                    {r.severity}
                  </Badge>
                </td>
                <td className="px-2 py-2">{r.businessUnitName ?? (r.businessUnitId ? r.businessUnitId : 'none')}</td>
                <td className="px-2 py-2 font-mono text-[11px]">
                  {r.requestId ? <a href={`/detection/decisions/${encodeURIComponent(r.requestId)}`}>{r.requestId}</a> : 'pending'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function MdmExportView({ e }: { e: EndpointMediaMdmExport }) {
  return (
    <div className="space-y-2 text-xs" data-testid="endpoint-media-mdm">
      <p className="text-muted-foreground">{e.note}</p>
      <table className="w-full">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
            <th className="text-left px-2 py-2 font-medium">Platform</th>
            <th className="text-left px-2 py-2 font-medium">Where to set it</th>
            <th className="text-left px-2 py-2 font-medium">Setting</th>
            <th className="text-left px-2 py-2 font-medium">Value</th>
            <th className="text-left px-2 py-2 font-medium">What it does</th>
          </tr>
        </thead>
        <tbody>
          {e.settings.map((s) => (
            <tr key={s.setting} className="border-b border-border/30" data-testid="endpoint-media-mdm-setting">
              <td className="px-2 py-2">{s.platform === 'windows' ? 'Windows' : 'Linux'}</td>
              <td className="px-2 py-2 text-muted-foreground">{s.channel}</td>
              <td className="px-2 py-2 font-mono text-[11px] break-all">{s.setting}</td>
              <td className="px-2 py-2 font-mono text-[11px]">{s.value}</td>
              <td className="px-2 py-2 text-muted-foreground">{s.effect}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p data-testid="endpoint-media-mdm-count">
        {e.devices.length} laptop{e.devices.length === 1 ? '' : 's'} hold an open file of a restricted class
        {e.truncated ? ' (the list is cut at its limit; narrow the classes or contact support@foctta.com)' : ''}. macOS is not covered by these settings.
      </p>
    </div>
  );
}

export function EndpointMediaPanel() {
  const canWrite = useCan(MEDIA_POLICY_WRITE_PERMISSION);
  const [policy, setPolicy] = useState<Awaited<ReturnType<typeof getEndpointMediaPolicy>> | null>(null);
  const [summary, setSummary] = useState<EndpointMediaSummary | null>(null);
  const [mdm, setMdm] = useState<EndpointMediaMdmExport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [units, setUnits] = useState<Array<{ id: string; name: string }>>([]);
  const [scope, setScope] = useState('tenant');
  const [classes, setClasses] = useState('');
  const [classesInherit, setClassesInherit] = useState(true);
  const [scan, setScan] = useState<TriState>('inherit');
  const [watch, setWatch] = useState<TriState>('inherit');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const p = await getEndpointMediaPolicy();
      setPolicy(p);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    }
    try {
      setSummary(await getEndpointMediaSummary());
    } catch {
      // Detection may not be licensed, or the caller may not read Detection: the policy still shows.
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let live = true;
    listBusinessUnits({ status: 'active' })
      .then((r) => live && setUnits(r.businessUnits.map((u) => ({ id: u.id, name: u.name })).slice(0, 500)))
      .catch(() => live && setUnits([]));
    return () => {
      live = false;
    };
  }, []);

  // The editor shows the chosen level as it is stored.
  useEffect(() => {
    const row = scope === 'tenant' ? policy?.tenant : policy?.businessUnits.find((u) => u.businessUnitId === scope);
    setClasses((row?.restrictedClasses ?? []).join(', '));
    setClassesInherit(row ? row.restrictedClasses === null : true);
    setScan(triOf(row?.scanRemovable));
    setWatch(triOf(row?.watchDownloads));
  }, [scope, policy]);

  const parsed: ReturnType<typeof parseClasses> = classesInherit ? { ok: true, value: [] } : parseClasses(classes);
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 500;

  async function save(): Promise<void> {
    if (!parsed.ok || !reasonOk) return;
    setBusy(true);
    setSaved(null);
    try {
      await setEndpointMediaPolicy(editorBody({ scope, classesInherit, classes: parsed.value, scan, watch, reason }));
      setReason('');
      setSaved('Saved. Laptops take the removable media setting at their next check-in, and scan removable media from their next scan.');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setBusy(false);
    }
  }

  async function loadMdm(): Promise<void> {
    try {
      setMdm(await getEndpointMediaMdmExport());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'export failed');
    }
  }

  function downloadCsv(): void {
    if (!mdm) return;
    const url = URL.createObjectURL(new Blob([mdmCsv(mdm)], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'foctta-laptops-with-restricted-data.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mt-8 space-y-4" data-testid="endpoint-media-panel">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Usb className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Endpoint downloads and removable media</h2>
        </div>
        <MediaScopeNote statement={policy?.statement ?? null} />
      </div>
      {error && (
        <div className="glass-lite rounded-md p-2 text-xs text-sev-critical" role="alert">
          {error}
        </div>
      )}
      <GlassCard className="p-0 overflow-hidden">
        <div className="px-4 py-2 border-b border-border/40">
          <CardTitle className="text-sm">Settings</CardTitle>
          <CardDescription className="text-xs">A business unit&apos;s setting wins over the tenant&apos;s, key by key; &quot;inherits&quot; takes the tenant&apos;s.</CardDescription>
        </div>
        {policy === null && !error ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            Loading
          </div>
        ) : (
          <MediaPolicyTable tenant={policy?.tenant ?? null} units={policy?.businessUnits ?? []} />
        )}
        {canWrite && (
          <div className="p-4 space-y-2 border-t border-border/40 text-xs" data-testid="endpoint-media-editor">
            <label className="block">
              Level
              <select className={`${INPUT_CLASS} w-full mt-1`} value={scope} onChange={(e) => setScope(e.target.value)} data-testid="endpoint-media-scope-select">
                <option value="tenant">Whole tenant</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={classesInherit} onChange={(e) => setClassesInherit(e.target.checked)} data-testid="endpoint-media-classes-inherit" />
              {scope === 'tenant' ? 'No restricted classes set for the whole tenant' : "Use the tenant's restricted classes"}
            </label>
            {!classesInherit && (
              <label className="block">
                Restricted classes (classifier labels, comma separated)
                <input
                  className={`${INPUT_CLASS} w-full mt-1`}
                  value={classes}
                  onChange={(e) => setClasses(e.target.value)}
                  placeholder="aadhaar, pan, credit_card"
                  data-testid="endpoint-media-classes"
                />
              </label>
            )}
            {!parsed.ok && (
              <p className="text-sev-high" role="alert" data-testid="endpoint-media-classes-error">
                {parsed.error}
              </p>
            )}
            <label className="block">
              Scan removable media connected when a laptop scans (Windows and Linux agents; tell employees first)
              <select className={`${INPUT_CLASS} w-full mt-1`} value={scan} onChange={(e) => setScan(e.target.value as TriState)} data-testid="endpoint-media-scan">
                <option value="inherit">{scope === 'tenant' ? 'Not set (off)' : "Tenant's setting"}</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </label>
            <label className="block">
              Raise a finding for a restricted file found in a Downloads folder
              <select className={`${INPUT_CLASS} w-full mt-1`} value={watch} onChange={(e) => setWatch(e.target.value as TriState)} data-testid="endpoint-media-watch">
                <option value="inherit">{scope === 'tenant' ? 'Not set (off)' : "Tenant's setting"}</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </label>
            <label className="block">
              Reason (kept in the audit trail)
              <input className={`${INPUT_CLASS} w-full mt-1`} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} data-testid="endpoint-media-reason" />
            </label>
            <Button size="sm" disabled={busy || !parsed.ok || !reasonOk} onClick={() => void save()} data-testid="endpoint-media-save">
              {busy ? 'Saving' : 'Save'}
            </Button>
            {saved && <p className="text-muted-foreground">{saved}</p>}
          </div>
        )}
      </GlassCard>
      {summary && (
        <GlassCard className="p-4">
          <CardTitle className="text-sm mb-2">Findings</CardTitle>
          <MediaSummaryView s={summary} />
        </GlassCard>
      )}
      <GlassCard className="p-4 space-y-2">
        <CardTitle className="text-sm">Block writes to removable disks with your MDM</CardTitle>
        {mdm ? (
          <>
            <MdmExportView e={mdm} />
            <Button size="sm" variant="outline" onClick={downloadCsv} disabled={mdm.devices.length === 0} data-testid="endpoint-media-csv">
              <Download className="h-3 w-3 mr-1" />
              Download the laptop list (CSV)
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => void loadMdm()} data-testid="endpoint-media-mdm-load">
            Show the MDM setting and the laptops holding restricted data
          </Button>
        )}
      </GlassCard>
    </div>
  );
}
